/**
 * en-IN formatting for the Portfolio Planner — screen and PDF share these so a
 * figure never renders two ways.
 *
 * `rupee`/`rupees` prefix the ₹ sign, which the PDF's embedded Jura subset
 * carries (U+20B9). Anything printed through jsPDF must stay inside that subset
 * — see scripts/build-jura-pdf-fonts.py for the glyph list.
 */

const round = (value: number): number => Math.round(Number(value) || 0);

/**
 * Money is printed to two decimals throughout.
 *
 * Paise are not noise here: a bond settles at ₹10,65,670.01 and the client
 * reconciles that against the trade-floor quote, so rounding it to whole
 * rupees loses the digits they are actually checking. Two places everywhere is
 * also what keeps a column of figures aligned on the decimal point.
 */
const MONEY_DECIMALS = 2;

const toMoney = (value: number): string =>
  Math.abs(Number(value) || 0).toLocaleString("en-IN", {
    minimumFractionDigits: MONEY_DECIMALS,
    maximumFractionDigits: MONEY_DECIMALS,
  });

/**
 * The minus goes BEFORE the symbol, and it is a true minus.
 *
 * `toLocaleString` renders a negative as "₹-1,03,914.00" — a hyphen wedged
 * between the symbol and the digits, which reads as a dash rather than a sign.
 * The sign is applied here instead so every negative in the feature matches.
 */
const sign = (value: number): string => ((Number(value) || 0) < 0 ? "−" : "");

/** "₹39,67,185.81" — Indian grouping, two decimals. */
export const rupee = (value: number): string =>
  `${sign(value)}₹${toMoney(value)}`;

/** "39,67,185.81" — the same digits, without the currency symbol. */
export const rupees = (value: number): string =>
  `${sign(value)}${toMoney(value)}`;

/**
 * Retained as an alias only. There is no whole-rupee form any more — every
 * amount in this feature carries its paise, so a second money format would only
 * be a way to reintroduce one.
 */
export const rupeeWhole = (value: number): string => rupee(value);

/** "+₹4,672" / "−₹4,672", using the true minus sign rather than a hyphen. */
export const rupeeSigned = (value: number): string =>
  `${value < 0 ? "−" : "+"}${rupee(Math.abs(value))}`;

/**
 * Headline figures — KPI tiles, the running rail, section leads.
 *
 * THE SAME FORMAT AS EVERY OTHER AMOUNT. There used to be a magnitude rule here
 * that turned anything above ten lakh into "₹58.3 L", on the reasoning that a
 * headline is read rather than reconciled. That is not what this product wants:
 * a figure a client is going to check against a statement has to be the figure,
 * in full, to the paise — in the KPI tile as much as in the table under it.
 *
 * Kept as a named export rather than folded into `rupee` so the call sites
 * still say which figures are headlines, and so there is exactly one place to
 * change if that judgement is ever revisited.
 */
export const headline = (value: number): string => rupee(value);

/** Full rupees and paise. Named for its call sites, identical to `rupee`. */
export const rupeeShort = (value: number): string => rupee(value);

/**
 * Axis ticks — the same digits, without the currency sign.
 *
 * An axis carries the sign once in its caption, not on every tick, so this is
 * the only place the symbol is dropped. The digits themselves are not
 * abbreviated: a tick reading "58.3L" cannot be checked against anything.
 */
export const shortNumber = (value: number): string => rupees(value);

export const percent = (value: number | null, decimals = 1): string =>
  value == null ? "—" : `${Number(value).toFixed(decimals)}%`;

/** The SMEST score factors in priority order, as the report names them. */
export const SCORE_FACTOR_LABELS: Record<string, string> = {
  alpha: "alpha",
  std: "standard deviation",
  pe: "P/E",
  expense: "expense ratio",
  returns: "annualised returns",
  beta: "beta",
};

/**
 * "0–10, from alpha 29%, standard deviation 24%, …" for the assumptions table.
 * Lists whichever weights the plan carries, so a plan generated before P/E and
 * returns were added still reads correctly.
 */
export const smestWeightsText = (
  weights: Record<string, number | undefined>,
  neutralFill?: number
): string => {
  const parts = Object.keys(SCORE_FACTOR_LABELS)
    .filter((key) => weights[key] != null)
    .map((key) => {
      const label = `${SCORE_FACTOR_LABELS[key]} ${Math.round((weights[key] as number) * 100)}%`;
      return key === "pe" ? `${label} (not for debt funds)` : label;
    });
  const fill =
    neutralFill != null
      ? `. A factor with no data counts as average (${neutralFill.toFixed(2)})`
      : "";
  return `0–10, from ${parts.join(", ")}${fill}`;
};

/** "6y" / "6y 4m" — tenure the way the trace rows phrase it. */
export const yearsLabel = (years: number): string => {
  const whole = Math.floor(years);
  const months = Math.round((years - whole) * 12);
  return months ? `${whole}y ${months}m` : `${whole}y`;
};

/** "23 Jul 2026" from a YYYY-MM-DD or ISO timestamp; "—" when unparseable. */
export const reportDate = (value?: string | null): string => {
  if (!value) return "—";
  const parsed = new Date(value.includes("T") ? value : `${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

/* ─────────────────────── live money-input masking ───────────────────── */

/**
 * Indian digit grouping on a raw digit string: last three, then pairs.
 * Used by the form's money inputs, which mask as the user types while keeping
 * the raw numeric value alongside (rulebook page 2).
 */
export const groupIndian = (digits: string): string => {
  const clean = String(digits).replace(/\D/g, "").replace(/^0+(?=\d)/, "");
  if (clean === "") return "";
  if (clean.length <= 3) return clean;
  const last3 = clean.slice(-3);
  const rest = clean.slice(0, -3).replace(/\B(?=(\d{2})+(?!\d))/g, ",");
  return `${rest},${last3}`;
};

/** The inverse: strips the mask back to a number. */
export const parseIndian = (masked: string): number =>
  Number(String(masked).replace(/\D/g, "")) || 0;

/**
 * "SM-20260723-SHRE" — the plan reference printed on every page of the report.
 * Derived client-side from the generation timestamp and the client's name, so
 * a reprint of the same plan carries the same reference.
 */
export const planReference = (generatedAt: string, clientName: string): string => {
  const parsed = new Date(generatedAt);
  const stamp = Number.isNaN(parsed.getTime())
    ? "00000000"
    : `${parsed.getFullYear()}${String(parsed.getMonth() + 1).padStart(2, "0")}${String(
        parsed.getDate()
      ).padStart(2, "0")}`;
  const slug = (clientName || "CLIENT")
    .replace(/[^a-zA-Z]/g, "")
    .slice(0, 4)
    .toUpperCase()
    .padEnd(4, "X");
  return `SM-${stamp}-${slug}`;
};
